import moment from 'moment-timezone';
import { TableColumn } from "react-data-table-component";
import { currencyFormatter, customNumberFormatString, getCurrencySymbol } from '../../functions/general';
import { CustomField, SettingMultiPipelines, Settings } from '../../interfaces/Settings';
import { ReportTerminType } from '../../interfaces/ReportTermin';
import { IntlShape } from 'react-intl';
import { SimpleMeeting } from '../../interfaces/Meeting';
import { ListProduct, SimplePipeline } from '../../interfaces/Pipeline';
import { SimpleMerchant } from '../../interfaces/Merchant';
import { SimpleProduct } from '../../interfaces/Product';
import { Link } from 'react-router-dom';
import clsx from 'clsx';
import { Dropdown, Popover, Whisper, IconButton } from 'rsuite';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { ExpandableElement } from '../General/ExpandableElement';

interface CreateTableColumnsProps {
    intl: IntlShape;
    manageColumns: any[];
    customFieldTermin: CustomField;
    selectTemplateIdPipeline: string;
    simplePipelines: SimplePipeline[];
    simpleMerchants: SimpleMerchant[];
    simpleMeetings: SimpleMeeting[];
    simpleProducts: SimpleProduct[];
    settings: Settings;
    selectMultiPipeline: SettingMultiPipelines
    setShowAttachmentCustomFieldModal: (value: boolean) => void;
    setTerminData: (value: ReportTerminType) => void;
    setKey: (value: string) => void;
    setUrl: (value: string | string[]) => void;
}

export const createReportTerminTableColumns = ({
    intl,
    manageColumns,
    customFieldTermin,
    simplePipelines,
    simpleMerchants,
    simpleMeetings,
    simpleProducts,
    settings,
    setShowAttachmentCustomFieldModal,
    setTerminData,
    setKey,
    setUrl,
    selectTemplateIdPipeline,
    selectMultiPipeline


}: CreateTableColumnsProps): TableColumn<ReportTerminType>[] => {

    const baseTableColumns: TableColumn<ReportTerminType>[] = [
        {
            name: intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.ACTION' }),
            center: true,
            allowOverflow: true,
            width: '70px',
            id: 'fixedLeft',
            cell: (row) => {
                return (
                    <>
                        <Whisper
                            className={"rs-popover-no-padding"}
                            placement="autoVerticalStart"
                            trigger="click"
                            speaker={
                                <Popover arrow={false} style={{ padding: 0 }} >
                                    <Dropdown.Menu>
                                        <Dropdown.Item as="div">
                                            <Link
                                                target="_blank"
                                                replace
                                                className={clsx('menu-link without-sub')}
                                                to={`/pipelines/${selectTemplateIdPipeline}/log/${row.leadId}`}
                                            >
                                                <i className="fas fa-eye fs-4 me-3" />
                                                <span>
                                                    {intl.formatMessage({ id: 'FORM.LABEL.DETAILS' }, { title: settings.pipeline_title })}
                                                </span>
                                            </Link>
                                        </Dropdown.Item>
                                    </Dropdown.Menu>
                                </Popover>
                            }
                        >
                            <IconButton icon={<MoreVertIcon />} appearance="subtle" />
                        </Whisper>
                    </>
                )
            },
            omit: manageColumns.find(item => item.name === intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.ACTION' }))?.omit || false
        },
        {
            name: intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.NAME_TITLE' }, { title: settings.merchant_title }),
            id: 'merchantName',
            width: '270px',
            selector: row => row.merchantName,
            center: true,
            sortable: true,
            wrap: true,
            style: {
                whiteSpace: 'normal',
                overflow: 'hidden',
                textOverflow: 'ellipsis'
            },
            allowOverflow: true,
            reorder: true,
            omit: manageColumns.find(item => item.name === intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.NAME_TITLE' }, { title: settings.merchant_title }))?.omit || false
        },
        {
            name: intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.PRODUCT' }),
            id: 'products',
            width: '250px',
            cell: (row) => {
                const leadProducts = row.leadProducts
                if (leadProducts.length > 0) {
                    return (
                        <ExpandableElement
                            content={
                                <>
                                    {leadProducts.map((product: ListProduct, index: number) => {
                                        return (
                                            <ul key={index} className='w-100' style={{ listStyleType: 'disc', paddingLeft: '20px' }}>
                                                <li>
                                                    <span>
                                                        {product.productName} {product.unitName ? `(${product.unitName})` : '(PCS)'}
                                                    </span><br />
                                                    <span className='text-muted'>
                                                        {product.leadProductNotes && product.leadProductNotes !== '-' ? `*${product.leadProductNotes}` : ''}
                                                    </span>
                                                </li>
                                            </ul>
                                        )
                                    })}
                                </>
                            }
                            maxHeight={70} />
                    )
                } else {
                    return '-'
                }
            },
            center: true,
            sortable: true,
            wrap: true,
            style: {
                whiteSpace: 'normal',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                flexDirection: 'column',
                justifyContent: 'start',
            },
            allowOverflow: true,
            reorder: true,
            omit: manageColumns.find(item => item.name === intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.NAME_TITLE' }, { title: settings.merchant_title }))?.omit || false
        },
        {
            name: `Nominal Termin (${selectMultiPipeline.currencyCode})`,
            id: 'terminNominal',
            width: '170px',
            selector: row => row.terminNominal,
            sortable: true,
            wrap: true,
            style: {
                whiteSpace: 'normal'
            },
            allowOverflow: true,
            reorder: true,
            center: true,
            omit: manageColumns.find(item => item.name === `Nominal Termin (${selectMultiPipeline.currencyCode})`)?.omit || false,
            cell: (row) => {
                return (
                    <span className='d-flex justify-content-between' style={{ width: '100%' }}>
                        <span>{getCurrencySymbol(selectMultiPipeline.currencyCode)}</span>
                        <span>{row.terminNominal && currencyFormatter(row.terminNominal, selectMultiPipeline.currencyIsRounded, selectMultiPipeline.currencyCode)}</span>
                    </span>
                )
            }
        },
        {
            name: intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.DATE_DUE_TERMIN' }),
            id: 'tanggalJatuhTempo',
            width: '200px',
            selector: row => row.tanggalJatuhTempo,
            sortable: true,
            wrap: true,
            style: {
                whiteSpace: 'normal',
                overflow: 'hidden',
                textOverflow: 'ellipsis'
            },
            center: true,
            cell: (row) => {
                return (
                    <span className='d-flex justify-content-between' style={{ width: '100%' }}>
                        <span>{row.tanggalJatuhTempo ? moment(row.tanggalJatuhTempo).format('DD MMM YYYY') : '-'}</span>
                    </span>
                )
            },
            allowOverflow: true,
            reorder: true,
            omit: manageColumns.find(item => item.name === intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.DATE_DUE_TERMIN' }))?.omit || false
        },
        {
            name: intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.DATE_PAID_TERMIN' }),
            id: 'tanggalPembayaran',
            width: '200px',
            selector: row => row.tanggalPembayaran,
            sortable: true,
            wrap: true,
            style: {
                whiteSpace: 'normal',
                overflow: 'hidden',
                textOverflow: 'ellipsis'
            },
            center: true,
            cell: (row) => {
                return (
                    <span className='d-flex justify-content-between' style={{ width: '100%' }}>
                        <span>{row.tanggalPembayaran ? moment(row.tanggalPembayaran).format('DD MMM YYYY') : '-'}</span>
                    </span>
                )
            },
            allowOverflow: true,
            reorder: true,
            omit: manageColumns.find(item => item.name === intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.DATE_PAID_TERMIN' }))?.omit || false
        },
    ];

    const customFieldColumns: TableColumn<ReportTerminType>[] = Object.keys(customFieldTermin).map((key) => {
        if (customFieldTermin[key].type === 'date') {
            return {
                id: key,
                customField: true,
                name: customFieldTermin[key].name,
                selector: (row: ReportTerminType) => row.customFieldTermin && row.customFieldTermin[key] ? moment(row.customFieldTermin[key]).format('DD-MM-YYYY') : '-',
                sortable: true,
                reorder: true,
                center: true,
                width: "150px",
                omit: manageColumns.find(item => item.name === customFieldTermin[key].name)?.omit || false,
                cell: (row: ReportTerminType) => {
                    if (row.customFieldTermin && row.customFieldTermin[key]) {
                        return (
                            moment(row.customFieldTermin[key]).format("DD MMM YYYY")
                        )
                    } else {
                        return '-'
                    }
                }
            };
        } else if (customFieldTermin[key].type === 'image' || customFieldTermin[key].type === 'images') {
            return {
                id: key,
                width: "70px",
                customField: true,
                name: customFieldTermin[key].name,
                center: true,
                selector: (row: ReportTerminType) => {
                    return row.customFieldTermin && row.customFieldTermin[key] ? row.customFieldTermin[key] as string : '-'
                },
                cell: (row: ReportTerminType) => {
                    if (row.customFieldTermin && row.customFieldTermin[key]) {
                        return (
                            <span role="button" onClick={
                                () => {
                                    setShowAttachmentCustomFieldModal(true);
                                    setTerminData(row);
                                    setKey(key);
                                    setUrl(row.customFieldTermin[key] as any);
                                }
                            }>
                                <i className="fas fs-2 fa-eye"></i>
                            </span>
                        )
                    } else {
                        return '-'
                    }
                },
                reorder: true,
                omit: manageColumns.find(item => item.name === customFieldTermin[key].name)?.omit || false,
            }
        } else if (customFieldTermin[key].type === 'file' || customFieldTermin[key].type === 'files' || customFieldTermin[key].type === 'video' || customFieldTermin[key].type === 'videos') {
            return {
                id: key,
                width: "70px",
                customField: true,
                name: customFieldTermin[key].name,
                center: true,
                selector: (row: ReportTerminType) => {
                    return row.customFieldTermin && row.customFieldTermin[key] ? row.customFieldTermin[key] as string : '-'
                },
                cell(row: ReportTerminType) {
                    const fileUrl = row.customFieldTermin && row.customFieldTermin[key];
                    if (typeof fileUrl === "string") {
                        return fileUrl ? (
                            <a
                                href={fileUrl}
                                target="_blank"
                                rel="noreferrer"
                                className="ms-2 btn btn-sm btn-icon btn-bg-light btn-active-color-primary"
                            >
                                <i className="fas fa-file-invoice fs-3 float-end"></i>
                            </a>
                        ) : '-';
                    } else {
                        return fileUrl ? (
                            <span role="button" onClick={() => {
                                setShowAttachmentCustomFieldModal(true);
                                setTerminData(row);
                                setKey(key);
                                setUrl(fileUrl as string[]);
                            }}>
                                <i className="fas fa-file-invoice fs-3 float-end"></i>
                            </span>
                        ) : '-';
                    }
                },
                reorder: true,
                omit: manageColumns.find(item => item.name === customFieldTermin[key].name)?.omit || false,
            }
        } else if (customFieldTermin[key].type === 'multiple' || customFieldTermin[key].type === 'multipleOption') {
            return {
                id: key,
                customField: true,
                name: customFieldTermin[key].name,
                selector: (row: ReportTerminType) => row.customFieldTermin && row.customFieldTermin[key] ? (row.customFieldTermin[key] as string).toString() : '-',
                cell: (row: ReportTerminType) => {
                    const values = row.customFieldTermin && row.customFieldTermin[key] && row.customFieldTermin[key]?.toString()
                    if (customFieldTermin[key].type === 'multipleOption') {
                        return values ? (
                            <div style={{ whiteSpace: 'pre-wrap' }}>
                                <div>{values.split(',').join(', ')}</div>
                            </div>
                        ) : "-"
                    } else return values ? values.split(',').join(', ') : '-'
                },
                sortable: true,
                reorder: true,
                width: "150px",
                center: true,
                omit: manageColumns.find(item => item.name === customFieldTermin[key].name)?.omit || false,
            };
        } else if (customFieldTermin[key].type === 'link') {
            return {
                id: key,
                customField: true,
                name: customFieldTermin[key].name,
                grow: 2,
                cell: (row: ReportTerminType) => {
                    if (row.customFieldTermin && row.customFieldTermin[key]) {
                        return (
                            <a href={row.customFieldTermin[key] as string} rel="noreferrer" className="btn btn-sm btn-outline btn-outline-default btn-active-light text-gray-600" target="_blank">Lihat<i className="fas fa-external-link-alt fs-6 ms-2 p-0"></i></a>
                        )
                    } else return "-"
                },
                sortable: true,
                reorder: true,
                width: "150px",
                center: true,
                omit: manageColumns.find(item => item.name === customFieldTermin[key].name)?.omit,
            };
        } else if (customFieldTermin[key].type === 'serial') {
            return {
                id: key,
                customField: true,
                name: customFieldTermin[key].name,
                cell: (row: ReportTerminType) => {
                    const values = row.customFieldTermin && row.customFieldTermin[key];
                    return values ? (values as unknown as string[]).join('') : '-';
                },
                sortable: true,
                reorder: true,
                width: "150px",
                center: true,
                omit: manageColumns.find(item => item.name === customFieldTermin[key].name)?.omit || false,
            };
        } else if (customFieldTermin[key].type === 'number') {
            return {
                id: key,
                name: customFieldTermin[key].name,
                selector: (row: ReportTerminType) => row.customFieldTermin && row.customFieldTermin[key] ? customNumberFormatString(row.customFieldTermin[key] as unknown as string) : '-',
                sortable: true,
                reorder: true,
                width: "150px",
                center: true,
                omit: manageColumns.find(item => item.name === customFieldTermin[key].name)?.omit || false
            }
        } else if (customFieldTermin[key].type === 'dateTimeRange') {
            return {
                id: key,
                customField: true,
                name: customFieldTermin[key].name,
                selector: (row: ReportTerminType) => {
                    if (row.customFieldTermin && row.customFieldTermin[key]) {
                        return Array.isArray(row.customFieldTermin[key]) && (row.customFieldTermin[key] as string[]).map((item: any) => moment(item).format('DD MMM YYYY HH:mm')).join(' - ')
                    } else {
                        return '-'
                    }
                },
                width: "200px",
                cell: (row: ReportTerminType) => {
                    if (row.customFieldTermin && row.customFieldTermin[key]) {
                        return Array.isArray(row.customFieldTermin[key]) && (row.customFieldTermin[key] as string[]).map((item: any) => moment(item).format('DD MMM YYYY HH:mm')).join(' - ')
                    } else {
                        return '-'
                    }
                },
                sortable: true,
                reorder: true,
                center: true,
                omit: manageColumns.find(item => item.name === customFieldTermin[key].name)?.omit || false
            }
        } else if (customFieldTermin[key].type === 'time') {
            return {
                id: key,
                customField: true,
                name: customFieldTermin[key].name,
                width: '150px',
                selector: (row: ReportTerminType) => {
                    return row.customFieldTermin && row.customFieldTermin[key] ? moment(row.customFieldTermin[key]).format('HH:mm') : '-';
                },
                cell: (row: ReportTerminType) => {
                    return row.customFieldTermin && row.customFieldTermin[key] ? moment(row.customFieldTermin[key]).format('HH:mm') : '-';
                },
                sortable: true,
                reorder: true,
                center: true,
                omit: manageColumns.find(item => item.name === customFieldTermin[key].name)?.omit || false
            }
        } else if (customFieldTermin[key].type === 'dateTime') {
            return {
                id: key,
                name: customFieldTermin[key].name,
                selector: (row: ReportTerminType) => {
                    return row.customFieldTermin && row.customFieldTermin[key] ? moment(row.customFieldTermin[key]).format('DD MMM YYYY HH:mm') : '-'
                },
                cell: (row: ReportTerminType) => {
                    return row.customFieldTermin && row.customFieldTermin[key] ? moment(row.customFieldTermin[key]).format('DD MMM YYYY HH:mm') : '-'
                },
                sortable: true,
                reorder: true,
                width: "200px",
                center: true,
                omit: manageColumns.find(item => item.name === customFieldTermin[key].name)?.omit || false,
            };
        } else if (customFieldTermin[key].type === 'option') {
            return {
                id: key,
                customField: true,
                name: customFieldTermin[key].name,
                selector: (row: ReportTerminType) => {
                    const fieldValue = row.customFieldTermin && (row.customFieldTermin[key] as string);
                    const displayValue = fieldValue && typeof fieldValue === 'object' && Object.keys(fieldValue).length === 0
                        ? '-'
                        : fieldValue;

                    return displayValue ? displayValue : '-';
                },
                cell: (row: ReportTerminType) => {
                    const values = row.customFieldTermin && row.customFieldTermin[key] && row?.customFieldTermin[key]?.toString();
                    return values ? (
                        <div style={{ whiteSpace: 'pre-wrap' }}>
                            {values.split('\n').map((line, idx) => (
                                <div key={idx}>{line}</div>
                            ))}
                        </div>
                    ) : "-"
                },
                sortable: true,
                reorder: true,
                width: "150px",
                center: true,
                omit: manageColumns.find(item => item.name === customFieldTermin[key].name)?.omit || false,
            };
        } else if (customFieldTermin[key].type === 'pipeline') {
            return {
                id: key,
                customField: true,
                name: customFieldTermin[key].name,
                grow: 2,
                selector: (row: ReportTerminType) => {
                    return row.customFieldTermin && row.customFieldTermin[key] ? row.customFieldTermin[key] as string : '-'
                },
                cell: (row: ReportTerminType) => {
                    const values = row.customFieldTermin && row.customFieldTermin[key];
                    const findData = simplePipelines.find((item: SimplePipeline) => item.id === values);
                    if (findData) {
                        return (
                            <div className="d-flex flex-column">
                                <span><b>{findData.name}</b> / </span>
                                <span>{currencyFormatter(findData.nominal, selectMultiPipeline.currencyIsRounded, selectMultiPipeline.currencyCode as any, { showPrefix: true })} / </span>
                                <span>{moment(findData.dateCreated).format("DD MMM YYYY")}</span>
                            </div>
                        );
                    }
                    return '-';
                },
                sortable: true,
                reorder: true,
                width: "150px",
                center: true,
                omit: manageColumns.find(item => item.name === customFieldTermin[key].name)?.omit || false,
            };
        } else if (customFieldTermin[key].type === 'merchant') {
            return {
                id: key,
                customField: true,
                name: customFieldTermin[key].name,
                grow: 2,
                selector: (row: ReportTerminType) => {
                    const values = row.customFieldTermin && row.customFieldTermin[key];
                    return values ? (values as string[]).join(', ') : '-'
                },
                cell: (row: ReportTerminType) => {
                    const values = row.customFieldTermin && row.customFieldTermin[key];
                    const findData = simpleMerchants.filter((item: SimpleMerchant) => (values as string[])?.map((item: any) => item).includes(item.id));
                    if (findData.length > 0) {
                        return findData.map((item: SimpleMerchant, index: number) => (
                            <div key={item.id} className="d-flex flex-column">
                                <span><b>{item.name}</b> / </span>
                                <span>{item.phone} {index !== findData.length - 1 && ','}</span>
                            </div>
                        ));
                    }
                    return '-';
                },
                sortable: true,
                reorder: true,
                width: "150px",
                center: true,
                omit: manageColumns.find(item => item.name === customFieldTermin[key].name)?.omit || false
            };
        } else if (customFieldTermin[key].type === 'meeting') {
            return {
                id: key,
                customField: true,
                name: customFieldTermin[key].name,
                grow: 2,
                selector: (row: ReportTerminType) => {
                    const values = row.customFieldTermin && row.customFieldTermin[key];
                    return values ? (values as string[]).join(', ') : '-'
                },
                cell: (row: ReportTerminType) => {
                    const values = row.customFieldTermin && row.customFieldTermin[key];
                    const findData = simpleMeetings.filter((item: SimpleMeeting) => (values as string[])?.map((item: any) => item).includes(item.id));
                    if (findData.length > 0) {
                        return findData.map((item: SimpleMeeting, index: number) => (
                            <div key={item.id} className="d-flex flex-column">
                                <span><b>{item.name}</b> / </span>
                                <span>{moment(item.dateMeet).format('DD MMM YYYY')} {index !== findData.length - 1 && ','}</span>
                            </div>
                        ));
                    }
                    return '-';
                },
                sortable: true,
                reorder: true,
                width: "150px",
                center: true,
                omit: manageColumns.find(item => item.name === customFieldTermin[key].name)?.omit || false
            };
        } else if (customFieldTermin[key].type === 'product') {
            return {
                id: key,
                customField: true,
                name: customFieldTermin[key].name,
                grow: 2,
                selector: (row: ReportTerminType) => {
                    const values = row.customFieldTermin && row.customFieldTermin[key];
                    return values ? (values as string[]).join(', ') : '-'
                },
                cell: (row: ReportTerminType) => {
                    const values = row.customFieldTermin && row.customFieldTermin[key];
                    const findData = simpleProducts.filter((item: SimpleProduct) => (values as string[])?.map((item: any) => item).includes(item.id));
                    if (findData.length > 0) {
                        return findData.map((item: SimpleProduct, index: number) => (
                            <div key={item.id} className="d-flex flex-column">
                                <span><b>{item.name}</b> {index !== findData.length - 1 && ','}</span>
                            </div>
                        ));
                    }
                    return '-';
                },
                sortable: true,
                reorder: true,
                width: "150px",
                center: true,
                omit: manageColumns.find(item => item.name === customFieldTermin[key].name)?.omit || false
            };
        } else if (customFieldTermin[key].type === 'paragraph') {
            return {
                id: key,
                customField: true,
                name: customFieldTermin[key].name,
                grow: 2,
                cell: (row: ReportTerminType) => {
                    const values = row.customFieldTermin && row.customFieldTermin[key];
                    return values ? (
                        <ExpandableElement
                            content={
                                <div style={{ whiteSpace: 'pre-wrap' }}>
                                    {values}
                                </div>
                            }
                            maxHeight={100}
                        />
                    ) : "-"
                },
            }
        }

        return {
            id: key,
            customField: true,
            name: customFieldTermin[key].name,
            selector: (row: ReportTerminType) => {
                const fieldValue = row.customFieldTermin && row.customFieldTermin[key];
                const displayValue = fieldValue && typeof fieldValue === 'object' && Object.keys(fieldValue).length === 0
                    ? '-'
                    : fieldValue;

                return displayValue ? (displayValue as string).toLowerCase() : '-';
            },
            allowOverflow: true,
            sortable: true,
            width: "150px",
            omit: manageColumns.find(item => item.name === customFieldTermin[key].name)?.omit || false,
            cell: (row: ReportTerminType) => {
                const fieldValue = row.customFieldTermin && row.customFieldTermin[key];

                const displayValue = fieldValue && typeof fieldValue === 'object' && Object.keys(fieldValue).length === 0
                    ? '-'
                    : fieldValue;

                return displayValue || '-';
            },
            reorder: true
        };
    });

    return [
        ...baseTableColumns,
        ...customFieldColumns,
    ];
}; 